import {
  Box,
  Button,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ButtonComponent from "components/button/ButtonComponent";
import ExportButton from "components/button/ExportButton";
import TextFieldComponent from "components/input/TextFieldComponent";
import { formatCurrency } from "utils";
import LabelValueDisplay from "components/LabelValueDisplay";
import { MESSAGE } from "constants/validate";
import useItemPrice from "../hooks/useItemPrice";
import { ItemClientPrice, ItemStorePurchasePrice } from "../types/item";

const StyledLabelHead = styled("div")(({ theme }) => ({
  backgroundColor: "var(--primary-500)",
  textAlign: "center",
  padding: "10px 0",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  margin: "0px auto",
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: "var(--primary-500)",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledBodyTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: "center",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--offwhite-300)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--white)",
  },
}));

const Card = styled(Box)(({ theme }) => ({
  color: "var(--gray-700)",
  padding: "10px 21px",
  border: "1px solid var(--gray-250)",
  borderRadius: "4px",
  whiteSpace: "nowrap",
  fontSize: 20,
  [theme.breakpoints.up("md")]: { fontSize: 23 },
}));

const InputLabel = styled("label")(({ theme }) => ({
  color: "var(--black)",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
  fontWeight: "bold",
  marginRight: "33px",
  whiteSpace: "nowrap",
}));

/**
 * The `ItemPrice` component renders a page for managing item prices.
 * It includes a header, a main content area with item details, and a table of purchase prices.
 * There is also a modal for filtering the displayed data.
 * @example
 * return (
 *   <ItemPrice />
 * )
 * @returns {Element} The rendered component.
 * @remarks
 * The component uses several custom styled components such as `StyledLabelHead`, `StyledTableContainer`,
 * `StyledTableHead`, `StyledTableCell`, `StyledTableRow`, `StyledBodyTableCell`, `ExportButton`, and `CommonButton`.
 * It also uses Material-UI components like `Box`, `Typography`, `Table`, `TableRow`, `TableBody`, `TableCell`, `Button`,
 * `InputLabel`, and `TextField`.
 * @function handleOpenModal - Function to open the filter modal.
 * @function handleCloseModal - Function to close the filter modal.
 * @function handleFilter - Function to handle the filter action.
 */
function ItemPrice() {
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [edittingCell, setEdittingCell] = useState<{
    index: number;
    field: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const {
    item,
    itemStorePurchasePrice,
    itemClientPrice,
    searchId,
    setSearchId,
    fetchData,
    handleChangeStorePurchasePrice,
    handleChangeClientPrice,
    handleUpdateStorePurchasePrice,
    handleUpdateClientPrice,
  } = useItemPrice();

  const [formatedStorePurchasePrice, setFormatedStorePurchasePrice] = useState<any[]>([]);

  const [formatedClientPrice, setFormatedClientPrice] = useState<any[]>([]);

  const isCellEditting = (index: number, field: string) =>
    edittingCell?.index === index && edittingCell?.field === field;

  useEffect(() => {
    setFormatedStorePurchasePrice(
      itemStorePurchasePrice.map((itemData) => ({
        ...itemData,
        store_wholesale_price: formatCurrency(Number(itemData.store_wholesale_price)),
        store_purchase_price: formatCurrency(Number(itemData.store_purchase_price)),
      })),
    );
    setFormatedClientPrice(
      itemClientPrice.map((itemData) => ({
        ...itemData,
        client_wholesale_price: formatCurrency(Number(itemData.client_wholesale_price)),
      })),
    );
  }, [itemStorePurchasePrice, itemClientPrice]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFilter = () => {
    fetchData();
  };

  const onBlurStorePurchasePrice = (index: number, field: keyof ItemStorePurchasePrice) => {
    setEdittingCell(null);
    handleUpdateStorePurchasePrice(index, field);
  };

  const onBlurClientPrice = (index: number, field: keyof ItemClientPrice) => {
    setEdittingCell(null);
    handleUpdateClientPrice(index, field);
  };

  const onStartEdittingCell = (index: number, field: string, e: React.MouseEvent) => {
    setEdittingCell({ index, field });
    let target = e.target as HTMLElement;
    if (target.nodeName !== "TD") {
      target = target.closest("td") as HTMLElement;
    }
    setTimeout(() => {
      const inputTarget = target?.querySelector("input");
      inputTarget?.focus();
    }, 0);
  };

  return (
    <>
      <Box sx={{ backgroundColor: "var(--white)", p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          display="flex"
          sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, columnGap: { xs: "30px", md: "none" } }}
        >
          <Box>
            <Typography
              margin={0}
              textAlign="right"
              fontSize={{ xs: 24, md: 28, lg: 33 }}
              whiteSpace="nowrap"
              color="var(--gray-700)"
            >
              マスタ管理
            </Typography>
            <Typography
              margin={0}
              textAlign="right"
              fontSize={{ xs: 20, md: 28 }}
              color="var(--gray-700)"
            >
              商品価格
            </Typography>
          </Box>
          <Box
            flexGrow="1"
            display="flex"
            flexWrap="wrap"
            justifyContent="space-evenly"
            sx={{
              columnGap: { xs: "20px", lg: "150px" },
              marginTop: { xs: "30px", md: "0" },
            }}
          >
            <Box
              width={{ xs: "calc(100% - 410px - 20px)", lg: "calc(100% - 410px - 300px)" }}
              display="flex"
              flexDirection="column"
            >
              <LabelValueDisplay
                label="商品コード"
                value={item?.item_cd ?? ""}
                type="text"
                containerSx={{ width: { xs: "auto", lg: "230px" } }}
                valueSx={{ justifyContent: "flex-start" }}
              />
              <LabelValueDisplay
                label="商品名"
                value={item?.item_name ?? ""}
                type="text"
                containerSx={{ width: { xs: "100%", lg: "80%" } }}
                labelSx={{ justifyContent: "left", pl: 8 }}
                valueSx={{ justifyContent: "flex-start" }}
              />
            </Box>
            <Box
              display="flex"
              columnGap="4px"
              width="410px"
              flexWrap="wrap"
              paddingRight={{ xs: 0, lg: "150px" }}
            >
              <LabelValueDisplay
                label="メイン仕入先名"
                value={item?.main_vendor_cd ?? ""}
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
              <LabelValueDisplay
                label="2時仕入先名"
                value=""
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
              <LabelValueDisplay
                label="仕入原価"
                value={item?.main_purchase_cost ? formatCurrency(item.main_purchase_cost) : ""}
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
              <LabelValueDisplay
                label="2次仕入原価"
                value=""
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
              <LabelValueDisplay
                label="店舗卸価格"
                value=""
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
              <LabelValueDisplay
                label="得意先卸"
                value=""
                type="text"
                containerSx={{ width: "calc((100% - 4px) / 2)" }}
                valueSx={{ textAlign: "center" }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-evenly"
        padding={{ xs: "20px 0px", md: "40px 0px" }}
        sx={{ backgroundColor: "var(--offwhite-100)", p: { xs: 2, md: 2.5, lg: 5 }, gap: 5 }}
      >
        <Box sx={{ width: { xs: "100%", lg: "calc(100% - 390px - 40px)" } }}>
          <Box display="flex" alignItems="center" margin={{ xs: "20px 0px", md: "40px 0px" }}>
            <Box
              fontSize={{ xs: 20, md: 28 }}
              fontWeight="medium"
              color="var(--gray-700)"
              marginRight="75px"
            >
              個別設定
            </Box>
            <ExportButton label="絞り込み" onClick={handleOpenModal} />
          </Box>
          {item && (
            <StyledTableContainer>
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: "18.1%" }}>店舗コード</StyledTableCell>
                    <StyledTableCell sx={{ width: "16.5%" }}>店舗卸価格</StyledTableCell>
                    <StyledTableCell sx={{ width: "16.5%" }}>店舗仕入先</StyledTableCell>
                    <StyledTableCell sx={{ width: "17%" }}>店舗仕入価格</StyledTableCell>
                    <StyledTableCell sx={{ width: "15.4%" }}>得意先コード</StyledTableCell>
                    <StyledTableCell sx={{ width: "16.5%" }}>得意先卸価格</StyledTableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {formatedStorePurchasePrice.map((purchasePrice, index) => (
                    <StyledTableRow
                      key={purchasePrice.id}
                      className={index % 2 === 0 ? "even" : "odd"}
                    >
                      <StyledBodyTableCell sx={{ width: "18.1%" }}>
                        {purchasePrice.store_cd}
                      </StyledBodyTableCell>
                      <StyledBodyTableCell
                        sx={{ width: "16.5%" }}
                        onClick={(e) => onStartEdittingCell(index, "store_wholesale_price", e)}
                      >
                        {isCellEditting(index, "store_wholesale_price") ? (
                          <TextFieldComponent
                            type="string"
                            sxBox={{
                              display: "inline-block",
                            }}
                            sxRoot={{ height: "40px" }}
                            sxNotchedOutline={{
                              border: !errors[`${index}-store_wholesale_price`]
                                ? "1px solid var(--silver-250)"
                                : "1px solid var(--red-800)",
                              borderRadius: 0,
                            }}
                            validateOption={{
                              required: {
                                message: MESSAGE.EMPTY,
                              },
                              maxLength: {
                                value: 9,
                                message: MESSAGE.MAX_LENGTH(9),
                              },
                            }}
                            isHiddenError
                            onError={(error) => {
                              setErrors((prev) => ({
                                ...prev,
                                [`${index}-store_wholesale_price`]: error,
                              }));
                              if (!error) {
                                const updatedErrors = { ...errors };
                                delete updatedErrors[`${index}-store_wholesale_price`];
                                setErrors(updatedErrors);
                              }
                            }}
                            value={itemStorePurchasePrice[index].store_wholesale_price}
                            sxInput={{
                              fontSize: { xs: 16, md: 18, lg: 20 },
                              width: "100%",
                              height: "100%",
                              padding: "10px 14px",
                            }}
                            onChangeText={(value) => {
                              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                              handleChangeStorePurchasePrice(
                                index,
                                "store_wholesale_price",
                                value.replace(/[^0-9]/g, ""),
                              );
                            }}
                            onBlur={() =>
                              !errors[`${index}-store_wholesale_price`] &&
                              onBlurStorePurchasePrice(index, "store_wholesale_price")
                            }
                          />
                        ) : (
                          <Box
                            sx={{
                              fontSize: { xs: 16, md: 18, lg: 20 },
                              textAlign: "center",
                              lineHeight: "40px",
                            }}
                          >
                            {purchasePrice.store_wholesale_price}
                          </Box>
                        )}
                      </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "16.5%" }}>
                        {purchasePrice.vendor_cd}
                      </StyledBodyTableCell>
                      <StyledBodyTableCell
                        sx={{ width: "17%" }}
                        onClick={(e) => onStartEdittingCell(index, "store_purchase_price", e)}
                      >
                        {isCellEditting(index, "store_purchase_price") ? (
                          <TextFieldComponent
                            sxBox={{
                              display: "inline-block",
                            }}
                            sxRoot={{ height: "40px" }}
                            sxNotchedOutline={{
                              border: !errors[`${index}-store_purchase_price`]
                                ? "1px solid var(--silver-250)"
                                : "1px solid var(--red-800)",
                              borderRadius: 0,
                            }}
                            validateOption={{
                              required: {
                                message: MESSAGE.EMPTY,
                              },
                              maxLength: {
                                value: 9,
                                message: MESSAGE.MAX_LENGTH(9),
                              },
                            }}
                            isHiddenError
                            onError={(error) => {
                              setErrors((prev) => ({
                                ...prev,
                                [`${index}-store_purchase_price`]: error,
                              }));
                              if (!error) {
                                const updatedErrors = { ...errors };
                                delete updatedErrors[`${index}-store_purchase_price`];
                                setErrors(updatedErrors);
                              }
                            }}
                            value={itemStorePurchasePrice[index].store_purchase_price}
                            sxInput={{
                              fontSize: { xs: 16, md: 18, lg: 20 },
                              width: "100%",
                              height: "100%",
                              padding: "10px 14px",
                            }}
                            onChangeText={(value) => {
                              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                              handleChangeStorePurchasePrice(
                                index,
                                "store_purchase_price",
                                value.replace(/[^0-9]/g, ""),
                              );
                            }}
                            onBlur={() =>
                              !errors[`${index}-store_purchase_price`] &&
                              onBlurStorePurchasePrice(index, "store_purchase_price")
                            }
                          />
                        ) : (
                          <Box
                            sx={{
                              fontSize: { xs: 16, md: 18, lg: 20 },
                              textAlign: "center",
                              lineHeight: "40px",
                            }}
                          >
                            {purchasePrice.store_purchase_price}
                          </Box>
                        )}
                      </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "15.4%" }}> </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "16.5%" }}> </StyledBodyTableCell>
                    </StyledTableRow>
                  ))}
                  {formatedClientPrice.map((purchasePrice, index) => (
                    <StyledTableRow
                      key={purchasePrice.id}
                      className={index % 2 === 0 ? "even" : "odd"}
                    >
                      <StyledBodyTableCell sx={{ width: "18.1%" }}> </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "16.5%" }}> </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "16.5%" }}> </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "17%" }}> </StyledBodyTableCell>
                      <StyledBodyTableCell sx={{ width: "15.4%" }}>
                        {purchasePrice.client_cd}
                      </StyledBodyTableCell>
                      <StyledBodyTableCell
                        sx={{ width: "16.5%" }}
                        onClick={(e) => onStartEdittingCell(index, "client_wholesale_price", e)}
                      >
                        {isCellEditting(index, "client_wholesale_price") ? (
                          <TextFieldComponent
                            sxBox={{
                              display: "inline-block",
                            }}
                            sxNotchedOutline={{
                              border: !errors[`${index}-client_wholesale_price`]
                                ? "2px solid var(--silver-250)"
                                : "2px solid var(--red-800)",
                              borderRadius: 0,
                            }}
                            validateOption={{
                              required: {
                                message: MESSAGE.EMPTY,
                              },
                              maxLength: {
                                value: 9,
                                message: MESSAGE.MAX_LENGTH(9),
                              },
                            }}
                            isHiddenError
                            onError={(error) => {
                              setErrors((prev) => ({
                                ...prev,
                                [`${index}-client_wholesale_price`]: error,
                              }));
                              if (!error) {
                                const updatedErrors = { ...errors };
                                delete updatedErrors[`${index}-client_wholesale_price`];
                                setErrors(updatedErrors);
                              }
                            }}
                            value={itemClientPrice[index].client_wholesale_price}
                            sxInput={{ fontSize: { xs: 16, md: 18, lg: 20 }, width: "100%" }}
                            onChangeText={(value) => {
                              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                              handleChangeClientPrice(
                                index,
                                "client_wholesale_price",
                                value.replace(/[^0-9]/g, ""),
                              );
                            }}
                            onBlur={() =>
                              !errors[`${index}-client_wholesale_price`] &&
                              onBlurClientPrice(index, "client_wholesale_price")
                            }
                          />
                        ) : (
                          <Box
                            sx={{
                              fontSize: { xs: 16, md: 18, lg: 20 },
                              textAlign: "center",
                              lineHeight: "40px",
                            }}
                          >
                            {purchasePrice.client_wholesale_price}
                          </Box>
                        )}
                      </StyledBodyTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Box>
        {isModalOpen && (
          <Box
            maxWidth="390px"
            sx={{
              marginTop: { xs: "30px", md: "30px", lg: "0px" },
              backgroundColor: "var(--white)",
            }}
          >
            <Box
              border="3px solid var(--gray-250)"
              padding="26px 38px 38px 38px"
              boxShadow="3px 3px 6px var(--shadow-sm-overlay)"
              borderRadius="21px"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom="40px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: 24, md: 28, lg: 33 },
                    fontWeight: "medium",
                    color: "var(--gray-700)",
                  }}
                >
                  絞り込み
                </Typography>
                <Button
                  sx={{
                    fontSize: { xs: 20, md: 25 },
                    fontWeight: "medium",
                    color: "var(--gray-700)",
                    border: "3px solid var(--gray-250)",
                    borderRadius: "10px",
                    width: "106px",
                    height: "62px",
                  }}
                  onClick={handleCloseModal}
                >
                  閉じる
                </Button>
              </Box>
              <Box display="flex" columnGap="9px" rowGap="25px" flexWrap="wrap" marginBottom="35px">
                <Card>卸価格</Card>
                <Card>仕入価格</Card>
                <Card>店舗</Card>
                <Card>得意先</Card>
              </Box>
              <Box display="flex" alignItems="center" marginBottom="38px">
                <InputLabel htmlFor="code">コード</InputLabel>
                <TextField
                  id="code"
                  label=""
                  variant="outlined"
                  sx={{ width: "calc(100% - 100px)" }}
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                />
              </Box>
              <ButtonComponent
                theme="dark"
                label="絞り込み"
                sx={{ width: "165px" }}
                onClick={handleFilter}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export default ItemPrice;
